import { Box, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { Button, Modal, Tab, Tabs } from '@feathr/components';
import { useFlags } from '@feathr/extender/state';

import EmailActivityTab from './EmailActivityTab';
import { useGroupExportModal } from './GroupExportModal.hooks';
import type {
  IEmailActivityExportPayload,
  IOtherActivitiesExportPayload,
} from './GroupExportModal.utils';
import OtherActivitiesTab from './OtherActivitiesTab';
import PersonTab from './PersonTab';
import SimpleExportForm from './SimpleExportForm';

import * as styles from './GroupExportModal.css';

export interface IGroupExportModalProps {
  opened: boolean;
  onClose: () => void;
  segment: Segment;
  /** Overrides the live export call; the modal wires the real one when omitted. */
  onExportEmailActivity?: (payload: IEmailActivityExportPayload) => Promise<void>;
  onExportOtherActivities?: (payload: IOtherActivitiesExportPayload) => Promise<void>;
  /** Overrides the "View export" action the success toast renders by default. */
  successToastAction?: IToastAction;
}

function GroupExportModal({
  opened,
  onClose,
  segment,
  onExportEmailActivity,
  onExportOtherActivities,
  successToastAction,
}: Readonly<IGroupExportModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { showEmailActivityExport } = useFlags();
  const {
    activeTab,
    setActiveTab,
    isLoading,
    isSubmitDisabled,
    personTab,
    emailActivityTab,
    otherActivitiesTab,
    simpleExportForm,
    handleSubmit,
    handleClose,
  } = useGroupExportModal({
    segment,
    onClose,
    onExportEmailActivity,
    onExportOtherActivities,
    successToastAction,
  });

  // Block dismissal (Cancel, overlay click, Esc) while a submit is in flight so
  // the toast never lands on an already-closed modal.
  const handleGuardedClose = useCallback((): void => {
    if (isLoading) {
      return;
    }
    handleClose();
  }, [handleClose, isLoading]);

  function handleSelectPersonTab(): void {
    setActiveTab('person');
  }

  function handleSelectEmailActivityTab(): void {
    setActiveTab('emailActivity');
  }

  function handleSelectOtherActivitiesTab(): void {
    setActiveTab('otherActivities');
  }

  return (
    <Modal
      bodyClassName={styles.body}
      description={t(
        'Export the data in the chosen group as a .csv file. When your export file is ready, we will send you an email with a download link.',
      )}
      onClose={handleGuardedClose}
      opened={opened}
      rightActions={[
        <Button
          disabled={isLoading}
          key={'cancel'}
          name={'group-export-cancel'}
          onClick={handleGuardedClose}
        >
          {t('Cancel')}
        </Button>,
        // Named because a submitting Button swaps its label for a spinner, which
        // leaves it with no accessible name to select by while a submit is in flight.
        <Button
          disabled={isSubmitDisabled}
          isLoading={isLoading}
          key={'confirm'}
          name={'group-export-submit'}
          onClick={handleSubmit}
          type={'primary'}
        >
          {t('Export')}
        </Button>,
      ]}
      title={t('Export Group')}
    >
      {showEmailActivityExport ? (
        <>
          <Tabs className={styles.tabs}>
            <Tab
              active={activeTab === 'person'}
              onClick={handleSelectPersonTab}
              title={t('Person')}
            />
            <Tab
              active={activeTab === 'emailActivity'}
              onClick={handleSelectEmailActivityTab}
              title={t('Email Activity')}
            />
            <Tab
              active={activeTab === 'otherActivities'}
              onClick={handleSelectOtherActivitiesTab}
              title={t('Other Activities')}
            />
          </Tabs>
          {activeTab === 'person' && <PersonTab state={personTab.state} />}
          {activeTab === 'emailActivity' && <EmailActivityTab state={emailActivityTab.state} />}
          {activeTab === 'otherActivities' && (
            <OtherActivitiesTab state={otherActivitiesTab.state} />
          )}
        </>
      ) : (
        <Box mt={theme.spacing['3']}>
          <SimpleExportForm state={simpleExportForm.state} />
        </Box>
      )}
    </Modal>
  );
}

export default observer(GroupExportModal);
